import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import PropTypes from "prop-types";
import { Form } from "react-bootstrap";
import { fetchProgramsDropdown } from "pages/AdminManage/actions";
import { isArray, isEmpty, map, isEqual } from "lodash";
import { CustomDropdownContainer } from "CommonComponent";

const matchesId = (sourceId, targetId) => String(sourceId) === String(targetId);

const findById = (list, key, value) =>
  (list || []).find((item) => matchesId(item?.[key], value));

const ProgramDropdown = ({
  selectedId = "",
  selectedChangingId = "",
  programValue,
  setProgramValue,
  programOnChange,
  setAllProgramIds,
  showSelectAllOption = true,
  isMulti = true,
  placeholder = "Select Program",
  label = "Program",
  name = "program_id",
  error = "",
  trackingProgram,
  forceRender,
  loadUserPrograms = false,
  loadManagerPrograms = false,
  user_id = null,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const session = useSelector((s) => s.session);
  const programList = useSelector(
    (state) => state.programState.programsDropdownList
  );
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (session?.current_page_permissions?.page_url === location.pathname) {
      loadPrograms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.current_page_permissions]);
  useEffect(() => {
    if (forceRender) {
      loadPrograms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceRender]);

  useEffect(() => {
    if (!selectedChangingId || !programList) return;

    if (isArray(selectedChangingId)) {
      const selected = selectedChangingId
        .map((id) => findById(programList, "id", id))
        .filter(Boolean)
        .map((item) => ({ value: item.id, label: item.name }));
      setProgramValue(selected);
    } else {
      const selected = findById(programList, "id", selectedChangingId);
      setProgramValue(
        selected ? { value: selected.id, label: selected.name } : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChangingId, programList]);

  useEffect(() => {
    if (!programList) return;

    const formattedOptions = map(programList, (item) => ({
      value: item.id,
      label: item.name,
    }));



    if (showSelectAllOption && isMulti) {
      formattedOptions.unshift({ value: "all", label: "All" });
    }

    setOptions(formattedOptions);

    if (typeof setAllProgramIds === "function") {
      setAllProgramIds(map(programList, "id"));
    }

    if (!isEmpty(selectedId)) {
      if (isArray(selectedId)) {
        const selected = selectedId
          .map((id) => findById(formattedOptions, "value", id))
          .filter(Boolean);

        if (!isEqual(programValue, selected)) {
          setProgramValue(selected);
        }
      } else {
        const selected = findById(formattedOptions, "value", selectedId);
        if (!isEqual(programValue, selected)) {
          setProgramValue(selected || null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programList, selectedId, isMulti, showSelectAllOption]);

  const loadPrograms = () => {
    let params = {
      object_viewed_id: session?.current_page_permissions?.object_id || "",
      load_manager_programs: loadManagerPrograms,
    };
    if (trackingProgram !== undefined) {
      params.tracking_program = trackingProgram;
    }

    if (loadUserPrograms !== undefined) {
      params.load_user_programs = loadUserPrograms;
      params.user_id = user_id;
    }

    dispatch(fetchProgramsDropdown({ ...params }));
  };

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label className="label-input">{label}</Form.Label>
        <Select
          name={name}
          options={options}
          value={programValue}
          onChange={programOnChange}
          isMulti={isMulti}
          placeholder={placeholder}
          className="multi-select w-100"
          classNamePrefix="react-select"
          isSearchable
          components={{ ValueContainer: CustomDropdownContainer }}
        />
        {error && <div className="invalid-feedback d-block">{error}</div>}
      </Form.Group>
    </>
  );
};

ProgramDropdown.propTypes = {
  programOnChange: PropTypes.func.isRequired,
  setProgramValue: PropTypes.func.isRequired,
  programValue: PropTypes.any,
  selectedId: PropTypes.any,
  selectedChangingId: PropTypes.any,
  setAllProgramIds: PropTypes.func,
  isMulti: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  error: PropTypes.string,
};

export default ProgramDropdown;
